import {Dispatch, FC, SetStateAction, useEffect, useState} from 'react'
import axios from 'axios'
import clsx from 'clsx'
import {InvoicePaymentForm, InvoicePaymentTypeItems} from '../../core/_models'
import {API_URL, fixZero, isNotEmpty} from '../../../../../../../_metronic/helpers'
import {CustomInputs} from '../../../../../../../_metronic/helpers/components/CustomInputs'
import {useListView} from '../../core/ListViewProvider'
import {ProductEditModal} from '../../../../product-management/products-list/product-edit-modal/ProductEditModal'
import {
  SearchSelect,
  SearchSelectOption,
} from '../../../../../../../_metronic/helpers/components/SearchSelect'
import {Uploader} from '../../../../../../../_metronic/helpers/components/Uploader'
import {UploadedFile} from '_metronic/helpers/components/uploadTypes'
import {
  getPaymentTaxValidationError,
  isPaymentDateBeforeIssue,
  isPaymentDateInFuture,
  nowPersianDateTime,
  PAYMENT_BEFORE_ISSUE_ERROR,
  PAYMENT_IN_FUTURE_ERROR,
} from '../../core/paymentTaxValidation'
type Props = {
  paymentItem: {
    paymentItem: InvoicePaymentForm | null | undefined
    setPaymentItem: React.Dispatch<React.SetStateAction<InvoicePaymentForm | null | undefined>>
  }
  payments?: {
    payments: InvoicePaymentForm[]
    setPayments: Dispatch<SetStateAction<InvoicePaymentForm[]>>
  }
  getSumNetPrice?: () => number
  getSumPayments?: () => number
  invoiceDate?: string
  // صورتحساب ارسال‌شده: تاریخ پرداخت نباید قبل از تاریخ و زمان صدور (invoiceDate) و نه بعد از اکنون باشد
  enforceInvoiceDate?: boolean
  isDisabled?: boolean
  totalVatRate?: number
}

const InvoicePaymentEditModalForm: FC<Props> = ({
  paymentItem,
  payments,
  getSumNetPrice,
  getSumPayments,
  invoiceDate,
  enforceInvoiceDate = false,
  isDisabled,
  totalVatRate = 0,
}) => {
  const [first, setFirst] = useState(true)
  const isExistingPayment = !!paymentItem.paymentItem && isNotEmpty(paymentItem.paymentItem)
  const [loaded, setLoaded] = useState(!isExistingPayment)
  // The tax-registration warning waits until a field is left (509): it showed «… معتبر نیست» before anything was
  // touched. A stored payment shows it at once, and a saved one shows it on its row in «روش تسویه».
  const [taxWarningVisible, setTaxWarningVisible] = useState(isExistingPayment)
  const [type, setType] = useState<string>('3')
  const [referenceNo, setReferenceNo] = useState<string>('')
  const [cardNo, setCardNo] = useState<string>('')
  const [payerId, setPayerId] = useState<string>('')
  const [switchNumber, setSwitchNumber] = useState<string>('')
  const [acquirerNumber, setAcquirerNumber] = useState<string>('')
  const [terminalNumber, setTerminalNumber] = useState<string>('')
  const [onlinePosTransactionId, setOnlinePosTransactionId] = useState<number | undefined>()
  // A new payment starts on the invoice's own date and time and on what is left to pay (509); it was an empty date
  // and «۰». A stored payment loads its own values below.
  const [date, setDate] = useState<string>(() =>
    isExistingPayment ? '' : (invoiceDate || '').trim()
  )
  const [price, setPrice] = useState<string>(() => {
    if (isExistingPayment || !getSumNetPrice) return ''
    const remaining = fixZero(
      Number(getSumNetPrice()) - Number(getSumPayments ? getSumPayments() : 0)
    )
    return remaining > 0 ? remaining.toString() : ''
  })
  const [cash, setCash] = useState<string>('')
  const [vat, setVat] = useState<string>('')
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [oldPrice, setOldPrice] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [pos, setPos] = useState<SearchSelectOption>()

  useEffect(() => {
    setVat(Math.floor(Number(price) * totalVatRate).toString())
    setCash((Number(price) - Math.floor(Number(price) * totalVatRate)).toString())
  }, [price])

  const getRemainPrice = () =>
    Number(getSumNetPrice ? getSumNetPrice() : 0) -
    Number(getSumPayments ? getSumPayments() : 0) +
    Number(oldPrice)

  const getPaymentMethodName = () => {
    switch (type) {
      case '4':
        return 'POS'
      case '5':
        return 'درگاه پرداخت الکترونیکی'
      default:
        return InvoicePaymentTypeItems.find((x) => x.value == type)?.label?.toString() || ''
    }
  }

  useEffect(() => {
    if (type != '4') {
      setOnlinePosTransactionId(undefined)
      setPos(undefined)
    }
  }, [type])

  useEffect(() => {
    if (!pos || type != '4') {
      if (!pos) {
        setOnlinePosTransactionId(undefined)
      }
      return
    }

    axios
      .post(`${API_URL}/taxpayer/pos-transactions/invoice-payment`, {
        transactionId: Number(pos.value),
        invoiceDate: invoiceDate || date,
      })
      .then((response) => {
        const payment = response.data.data || {}
        setOnlinePosTransactionId(Number(payment.onlinePosTransactionId || pos.value))
        setType((payment.type || 4).toString())
        setDate(payment.date || '')
        setPrice((payment.price || 0).toString())
        setReferenceNo(payment.traceNo || '')
        setCardNo(payment.cardNo || '')
        setPayerId(payment.payerNationalCode || '')
        setSwitchNumber(payment.switchNumber || '')
        setAcquirerNumber(payment.acquirerNumber || '')
        setTerminalNumber(payment.terminalNumber || '')
        setDescription(payment.description || '')
      })
  }, [pos])

  useEffect(() => {
    if (first) {
      if (paymentItem.paymentItem && isNotEmpty(paymentItem.paymentItem)) {
        setType(paymentItem.paymentItem.type)
        setReferenceNo(paymentItem.paymentItem.traceNo || paymentItem.paymentItem.refereceNo || '')
        setCardNo(paymentItem.paymentItem.cardNo || '')
        setPayerId(
          paymentItem.paymentItem.payerNationalCode || paymentItem.paymentItem.payerId || ''
        )
        setSwitchNumber(paymentItem.paymentItem.switchNumber || '')
        setAcquirerNumber(paymentItem.paymentItem.acquirerNumber || '')
        setTerminalNumber(paymentItem.paymentItem.terminalNumber || '')
        setOnlinePosTransactionId(paymentItem.paymentItem.onlinePosTransactionId)
        setDate(paymentItem.paymentItem.date)
        setPrice(paymentItem.paymentItem.price.toString())
        setDescription(paymentItem.paymentItem.description || '')
        setOldPrice(paymentItem.paymentItem.price.toString())
        setFiles(paymentItem.paymentItem.files || [])
        setLoaded(true)
      }
      setFirst(false)
    }
  }, [])

  const save = () => {
    const id =
      paymentItem.paymentItem && isNotEmpty(paymentItem.paymentItem)
        ? paymentItem.paymentItem.id
        : Math.floor(Math.random() * 952413) * -1
    const newValue: InvoicePaymentForm = {
      id: id,
      description: description,
      date: date,
      price: Number(price),
      cash: Number(cash),
      vat: Number(vat),
      cardNo: cardNo,
      payerId: payerId,
      payerNationalCode: payerId,
      refereceNo: referenceNo,
      traceNo: referenceNo,
      type: type,
      switchNumber: switchNumber,
      acquirerNumber: acquirerNumber,
      terminalNumber: terminalNumber,
      onlinePosTransactionId: onlinePosTransactionId,
      paymentDateTime: date,
      amount: Number(price),
      trackingNumber: referenceNo,
      payerCardNumber: cardNo,
      paymentMethod: getPaymentMethodName(),
      files: files,
      taxValidationError: getPaymentTaxValidationError({
        type,
        date,
        price: Number(price),
        traceNo: referenceNo,
        cardNo,
        payerNationalCode: payerId,
        switchNumber,
        acquirerNumber,
        terminalNumber,
        invoiceNetPrice: getSumNetPrice ? getSumNetPrice() : undefined,
        invoiceIssueDate: enforceInvoiceDate ? invoiceDate : undefined,
        nowDateTime: enforceInvoiceDate ? nowPersianDateTime() : undefined,
      }),
    }
    if (payments) {
      const ind = payments.payments.findIndex((x) => x.id == id)
      if (ind >= 0) {
        payments.payments.splice(ind, 1)
      }
      payments.setPayments([newValue, ...payments.payments])
    }
    paymentItem.setPaymentItem(undefined)
  }

  useEffect(() => {
    // An empty amount stays empty: Number('') made it «۰» before anything was typed (509).
    if (loaded && !onlinePosTransactionId && isNotEmpty(price)) {
      let _price = fixZero(Number(price))
      const remain: number = getRemainPrice()
      if (_price > remain) {
        _price = remain
      }
      setPrice(_price.toString())
    }
  }, [price])

  const getTimeTitle = () => {
    let title = ''
    switch (type) {
      case '1':
        title = 'دریافت چک'
        break
      case '2':
        title = 'تهاتر'
        break
      case '3':
        title = 'دریافت وجه'
        break
      case '4':
        title = ''
        break
      case '5':
        title = ''
        break
      case '6':
        title = ''
        break
      case '7':
        title = ''
        break
    }
    return title
  }
  const getPriceTitle = () => {
    let title = ''
    switch (type) {
      case '1':
        title = 'چک'
        break
      case '2':
        title = 'تهاتر'
        break
      case '3':
        title = ''
        break
      case '4':
        title = ''
        break
      case '5':
        title = ''
        break
      case '6':
        title = ''
        break
      case '7':
        title = ''
        break
    }
    return title
  }
  const isPosTransactionPayment = type == '4' && !!onlinePosTransactionId
  const hasPaymentTaxFields =
    isNotEmpty(price) ||
    isNotEmpty(date) ||
    isNotEmpty(referenceNo) ||
    isNotEmpty(cardNo) ||
    isNotEmpty(payerId) ||
    isNotEmpty(switchNumber) ||
    isNotEmpty(acquirerNumber) ||
    isNotEmpty(terminalNumber)
  const taxValidationError = hasPaymentTaxFields
    ? getPaymentTaxValidationError({
        type,
        date,
        price: Number(price),
        traceNo: referenceNo,
        cardNo,
        payerNationalCode: payerId,
        switchNumber,
        acquirerNumber,
        terminalNumber,
        invoiceNetPrice: getSumNetPrice ? getSumNetPrice() : undefined,
        invoiceIssueDate: enforceInvoiceDate ? invoiceDate : undefined,
        nowDateTime: enforceInvoiceDate ? nowPersianDateTime() : undefined,
      })
    : ''
  const dateBeforeIssue =
    enforceInvoiceDate && isNotEmpty(date) && isPaymentDateBeforeIssue(date, invoiceDate)
  const dateInFuture =
    enforceInvoiceDate &&
    isNotEmpty(date) &&
    !dateBeforeIssue &&
    isPaymentDateInFuture(date, nowPersianDateTime())
  const dateBlocked = dateBeforeIssue || dateInFuture

  const saveDisabled = !(
    isNotEmpty(price) &&
    isNotEmpty(date) &&
    price != '' &&
    price != '0' &&
    Number(price) > 0 &&
    !dateBlocked
  )

  return (
    <>
      {/* Leaving any field shows the tax warning; React's onBlur bubbles like focusout. */}
      <div className='row' onBlur={() => setTaxWarningVisible(true)}>
        <div className='col-12 mt-5'>
          <CustomInputs
            isDisabled={isDisabled}
            label='روش پرداخت'
            isRequired
            type='select'
            items={InvoicePaymentTypeItems}
            stateValue={{value: type, setValue: setType}}
          />
        </div>
        {type == '4' && (
          <div className='col-6 mt-5'>
            <SearchSelect
              isDisabled={isDisabled || !isNotEmpty(invoiceDate)}
              label='انتخاب تراکنش کارت‌خوان'
              placeholder='تراکنش آزاد همان روز را جستجو کنید...'
              section='onlinePosTransaction'
              stateValue={{value: pos, setValue: setPos}}
              extraData={{date: invoiceDate || date, price: getRemainPrice()}}
              hint='با انتخاب تراکنش، اطلاعات پرداخت به صورت خودکار از اطلاعات کارت‌خوان پر می‌شود.'
            >
              <a href='#' onClick={() => {}}>
                <i className='las la-plus-square fs-3x text-success'></i>
              </a>
            </SearchSelect>
          </div>
        )}
        {type == '5' && (
          <div className='col-6 mt-5'>
            <SearchSelect
              isDisabled={isDisabled}
              label='انتخاب درگاه پرداخت'
              section='pos'
              stateValue={{value: pos, setValue: setPos}}
            >
              <a href='#' onClick={() => {}}>
                <i className='las la-plus-square fs-3x text-success'></i>
              </a>
            </SearchSelect>
          </div>
        )}
        {(type == '6' || type == '7') && (
          <div className='col-6 mt-5'>
            <SearchSelect
              isDisabled={isDisabled}
              label='انتخاب حساب'
              section='pos'
              stateValue={{value: pos, setValue: setPos}}
            >
              <a href='#' onClick={() => {}}>
                <i className='las la-plus-square fs-3x text-success'></i>
              </a>
            </SearchSelect>
          </div>
        )}
        {(type == '4' || type == '5' || type == '6') && (
          <>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled || isPosTransactionPayment}
                label='شماره پیگیری/ شماره مرجع'
                type='text'
                stateValue={{value: referenceNo, setValue: setReferenceNo}}
              />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled || isPosTransactionPayment}
                label='شماره کارت پرداخت کننده'
                type='text'
                stateValue={{value: cardNo, setValue: setCardNo}}
              />
            </div>
          </>
        )}
        {(type == '4' || type == '5' || type == '6') && (
          <div className='col-6 mt-5'>
            <CustomInputs
              isDisabled={isDisabled || isPosTransactionPayment}
              label='شماره/ شناسه ملی/کد فراگیر پرداخت کننده'
              type='text'
              stateValue={{value: payerId, setValue: setPayerId}}
            />
          </div>
        )}
        {(type == '4' || type == '5' || type == '6') && (
          <>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled || isPosTransactionPayment}
                label='شماره سوئیچ پرداخت'
                type='text'
                stateValue={{value: switchNumber, setValue: setSwitchNumber}}
              />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled || isPosTransactionPayment}
                label='شماره پذیرنده فروشگاهی'
                type='text'
                stateValue={{value: acquirerNumber, setValue: setAcquirerNumber}}
              />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled || isPosTransactionPayment}
                label='شماره پایانه'
                type='text'
                stateValue={{value: terminalNumber, setValue: setTerminalNumber}}
              />
            </div>
          </>
        )}
        <div className='col-6 mt-5'>
          <CustomInputs
            isDisabled={isDisabled || isPosTransactionPayment}
            label={`تاریخ و زمان ${getTimeTitle()}`}
            isRequired
            type='datepicker'
            stateValue={{value: date, setValue: setDate}}
            limitFromDate={
              enforceInvoiceDate && invoiceDate ? invoiceDate.trim().substring(0, 10) : undefined
            }
            limitToDate={enforceInvoiceDate ? nowPersianDateTime().substring(0, 10) : undefined}
            timePicker={true}
          />
        </div>
        {type == '1' && (
          <>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled}
                label={`تاریخ چک`}
                isRequired
                type='datepicker'
              />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs
                isDisabled={isDisabled}
                label='بانک صادر کننده'
                isRequired
                type='select'
              />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs isDisabled={isDisabled} label='شعبه' isRequired type='text' />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs isDisabled={isDisabled} label='شماره حساب' isRequired type='text' />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs isDisabled={isDisabled} label='نام صاحب حساب' isRequired type='text' />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs isDisabled={isDisabled} label='شماره چک' isRequired type='text' />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs isDisabled={isDisabled} label='شماره صیادی' isRequired type='text' />
            </div>
            <div className='col-6 mt-5'>
              <CustomInputs isDisabled={isDisabled} label='در وجه' isRequired type='text' />
            </div>
          </>
        )}
        <div className='col-6 mt-5'>
          <CustomInputs
            isDisabled={isDisabled || isPosTransactionPayment}
            label={`مبلغ ${getPriceTitle()}`}
            isRequired
            type='currency'
            stateValue={{value: price, setValue: setPrice}}
          />
        </div>
        <div className='col-12 mt-5'>
          <CustomInputs
            isDisabled={isDisabled}
            label='توضیحات'
            type='textarea'
            stateValue={{value: description, setValue: setDescription}}
          />
        </div>
        <div className='col-12 mt-5'>
          <CustomInputs
            isDisabled={isDisabled}
            label='مستندات و ضمائم'
            uploaderProps={{
              label: 'مستندات',
              allowMultiple: true,
              maxFiles: 5,
              setFiles: setFiles,
              files: files,
              acceptedFileTypes: ['image/*', 'application/pdf', 'text/plain'],
            }}
            type='uploader'
          />
        </div>
        {dateBeforeIssue && (
          <div className='col-12 mt-5'>
            <div className='alert alert-danger py-3 mb-0'>
              <i className='las la-calendar-times fs-2 me-2'></i>
              {PAYMENT_BEFORE_ISSUE_ERROR} تاریخ صدور: <span dir='ltr'>{invoiceDate}</span>
            </div>
          </div>
        )}
        {dateInFuture && (
          <div className='col-12 mt-5'>
            <div className='alert alert-danger py-3 mb-0'>
              <i className='las la-calendar-times fs-2 me-2'></i>
              {PAYMENT_IN_FUTURE_ERROR} اکنون: <span dir='ltr'>{nowPersianDateTime()}</span>
            </div>
          </div>
        )}
        {taxValidationError && !dateBlocked && taxWarningVisible && (
          <div className='col-12 mt-5'>
            <div className='alert alert-warning py-3 mb-0' role='status'>
              <i className='las la-exclamation-triangle fs-2 me-2'></i>
              این ردیف پرداخت سمت سازمان ارسال نخواهد شد: {taxValidationError}
            </div>
          </div>
        )}
      </div>

      {/* begin::Actions */}
      {/* A sticky bar of one row (509): «ذخیره» wide first, «بستن» beside it; they were small and scrolled away. */}
      <div className='mp-invoice-form-actions mp-invoice-payment-actions'>
        {!isDisabled && (
          <button
            type='button'
            disabled={saveDisabled}
            className='btn btn-success flex-grow-1'
            onClick={save}
          >
            <span className='indicator-label'>ذخیره</span>
          </button>
        )}
        <button
          type='button'
          onClick={() => paymentItem.setPaymentItem(undefined)}
          className={clsx('btn btn-light', {'flex-grow-1': isDisabled})}
          data-kt-invoices-modal-action='cancel'
        >
          بستن
        </button>
      </div>
      {/* end::Actions */}
    </>
  )
}

export {InvoicePaymentEditModalForm}
